'use client'

// Social proof: two usage figures (the homepage's trust bento sets them in a
// tile of their own).
//
// The figure is PRESENT AT REST. It is in the server-rendered HTML, it is what
// the first paint shows, and it is what anyone who never scrolls, has JS off,
// asked for reduced motion, or is running a browser without IntersectionObserver
// reads. The count-up is an enhancement layered over that and nothing else
// depends on it.
//
// It shipped the other way round: state started at 0 and an IntersectionObserver
// was the ONLY thing that ever moved it. The strip sits below a tall hero, so on
// a laptop it is off-screen at load — and a visitor who did not scroll was told,
// in the largest numerals on the page, that this product has had 0 scans and 0
// users. The bug is invisible in every test that scrolls before it looks, which
// is every test we had.
//
// So the animation now only runs for a stat that starts OFF-SCREEN — one that is
// already in view has nothing to reveal, and counting it up would mean replacing
// a correct figure with a wrong one for 1.7 seconds.

import { useEffect, useRef, useState } from 'react'
import { USAGE_STATS, formatUsageFigure as format } from '@/lib/usageStats'

const DURATION = 1700

const easeOut = (x: number) => 1 - Math.pow(1 - x, 3)

function Stat({
  id,
  target,
  suffix,
  label,
}: {
  /** Stable test hook, independent of the label's wording. */
  id: string
  target: number
  suffix: string
  label: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  /** `null` is "nothing is animating": render the real figure. Every path that
   *  ends the animation returns here, so the strip always settles on the exact
   *  target rather than on the last eased sample. */
  const [animated, setAnimated] = useState<number | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') return
    // Optional call, not a bare one: every branch that cannot answer has to
    // leave the real figure standing, and a browser (or a test environment)
    // without `matchMedia` throwing here would take the whole strip down.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return

    let raf = 0
    // `null` rather than `0` as the "not started" sentinel: a rAF timestamp of
    // 0 is falsy, so `if (!start) start = now` re-baselines the animation on
    // every frame until the clock happens to be non-zero.
    let start: number | null = null
    const tick = (now: number) => {
      if (start === null) start = now
      const p = Math.min((now - start) / DURATION, 1)
      setAnimated(p < 1 ? target * easeOut(p) : null)
      if (p < 1) raf = requestAnimationFrame(tick)
    }

    // An observer's first callback reports the state at mount. That is the one
    // piece of information this needs: a stat visible then is left alone, and
    // only a stat that has to be scrolled to gets the count-up.
    let first = true
    const io = new IntersectionObserver(
      ([entry]) => {
        if (first) {
          first = false
          if (entry.isIntersecting) io.disconnect()
          return
        }
        if (!entry.isIntersecting) return
        io.disconnect()
        setAnimated(0)
        raf = requestAnimationFrame(tick)
      },
      { threshold: 0.5 },
    )
    io.observe(el)
    return () => {
      io.disconnect()
      if (raf) cancelAnimationFrame(raf)
    }
  }, [target])

  return (
    <div ref={ref}>
      <div
        data-testid={`social-proof-stat-${id}`}
        className="text-stat font-bold tabular-nums text-[var(--rt-ink)]"
      >
        {format(animated ?? target)}
        <span className="text-[var(--rt-ink-dim)]">{suffix}</span>
      </div>
      <div className="mt-2 text-body text-[var(--rt-ink-dim)]">{label}</div>
    </div>
  )
}

export default function SocialProof({ className = '' }: { className?: string }) {
  return (
    <div className={`flex flex-wrap gap-x-12 gap-y-6 ${className}`}>
      <Stat id="scans" target={USAGE_STATS.scans} suffix="+" label="simulations run" />
      <Stat id="unique-users" target={USAGE_STATS.users} suffix="+" label="people" />
    </div>
  )
}
